"use client";

import { useEffect, useRef } from "react";

/** Pull-to-refresh for locked scroll shells (body overflow:hidden). */
export function usePullToReload(selector = ".avt-frame, .md-frame, .site-shell-body") {
  const armed = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const nodes = Array.from(document.querySelectorAll(selector)) as HTMLElement[];
    if (!nodes.length) return;

    let startY = 0;
    let pulling = false;
    let target: HTMLElement | null = null;
    let indicator: HTMLDivElement | null = null;

    function ensureIndicator() {
      if (indicator) return indicator;
      indicator = document.createElement("div");
      indicator.setAttribute("data-pull-reload", "1");
      indicator.style.cssText =
        "position:fixed;left:50%;top:10px;transform:translateX(-50%) translateY(-120%);z-index:300000;" +
        "background:#E65100;color:#fff;font:650 12px/1 Poppins,sans-serif;padding:8px 14px;border-radius:999px;" +
        "box-shadow:0 8px 18px rgba(27,42,74,.18);opacity:0;transition:transform .15s ease,opacity .15s ease;pointer-events:none;";
      indicator.textContent = "Release to refresh";
      document.body.appendChild(indicator);
      return indicator;
    }

    function onStart(event: TouchEvent) {
      const el = nodes.find((node) => node.contains(event.target as Node) || node === event.target) || null;
      if (!el) return;
      if (el.scrollTop > 2) {
        pulling = false;
        return;
      }
      startY = event.touches[0]?.clientY || 0;
      pulling = true;
      target = el;
      armed.current = false;
    }

    function onMove(event: TouchEvent) {
      if (!pulling || !target) return;
      if (target.scrollTop > 2) {
        pulling = false;
        hide();
        return;
      }
      const y = event.touches[0]?.clientY || 0;
      const delta = y - startY;
      if (delta < 12) {
        armed.current = false;
        hide();
        return;
      }
      if (delta > 24 && event.cancelable) event.preventDefault();
      const tip = ensureIndicator();
      const progress = Math.min(1, (delta - 12) / 70);
      tip.style.opacity = String(0.35 + progress * 0.65);
      tip.style.transform = `translateX(-50%) translateY(${Math.min(28, delta * 0.25)}px)`;
      tip.textContent = delta > 78 ? "Release to refresh" : "Pull to refresh";
      armed.current = delta > 78;
    }

    function hide() {
      if (!indicator) return;
      indicator.style.opacity = "0";
      indicator.style.transform = "translateX(-50%) translateY(-120%)";
    }

    function onEnd() {
      if (armed.current) {
        hide();
        window.location.reload();
        return;
      }
      pulling = false;
      target = null;
      armed.current = false;
      hide();
    }

    nodes.forEach((node) => {
      node.addEventListener("touchstart", onStart, { passive: true });
      node.addEventListener("touchmove", onMove, { passive: false });
      node.addEventListener("touchend", onEnd, { passive: true });
      node.addEventListener("touchcancel", onEnd, { passive: true });
    });

    return () => {
      nodes.forEach((node) => {
        node.removeEventListener("touchstart", onStart);
        node.removeEventListener("touchmove", onMove);
        node.removeEventListener("touchend", onEnd);
        node.removeEventListener("touchcancel", onEnd);
      });
      indicator?.remove();
    };
  }, [selector]);
}
